import React from 'react';
import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  DownloadIcon,
  Dropdown,
  DropdownOverlay,
  Heading,
  RayIcon,
  SelectInput,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import type { PolicySummaryItem } from 'components/Settings/EmployeeLoansSettings/types';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { WHOLE_NUMBER_REGEX } from 'utils/regexes';
import { CreateLoanRequestFormData } from './types';
import { BASIC_LOAN_DATA_KEY } from './constants';
import { formatMonthYear } from '../utils';

const PolicySummaryPanel = ({
  policySummary,
  policyFileId,
}: {
  policySummary?: PolicySummaryItem[] | null;
  policyFileId?: string | null;
}) => {
  if (!policySummary || policySummary.length === 0) return null;
  return (
    <Box
      width="380px"
      height="100%"
      padding="spacing.7"
      borderLeftWidth="thin"
      borderColor="surface.border.gray.subtle"
      display="flex"
      flexDirection="column"
      gap="spacing.6"
      flexShrink={0}
      backgroundColor="surface.background.gray.moderate">
      <Box display="flex" alignItems="center" gap="spacing.3">
        <RayIcon color="surface.icon.primary.normal" />
        <Text size="small" weight="semibold" variant="body" color="surface.text.gray.normal">
          Policy Summary
        </Text>
      </Box>
      <Box display="flex" flexDirection="column" flex="1" overflowY="auto">
        {policySummary.map((item, idx) => (
          <Box key={idx}>
            <Box paddingY="spacing.3">
              <Text size="small" weight="semibold" color="surface.text.gray.subtle">
                {item.label}
              </Text>
              <Text size="small" color="surface.text.gray.muted" marginTop="spacing.1">
                {item.value}
              </Text>
            </Box>
            {idx < policySummary.length - 1 && (
              <Box borderBottomWidth="thin" borderColor="surface.border.gray.subtle" />
            )}
          </Box>
        ))}
      </Box>
      {policyFileId && (
        <Box display="flex" alignItems="end">
          <Button
            variant="tertiary"
            size="small"
            icon={DownloadIcon}
            isFullWidth
            onClick={() => {
              window.location.href = `/v2/api/loans/download/${policyFileId}`;
            }}>
            Download Policy Document
          </Button>
        </Box>
      )}
    </Box>
  );
};

const AmountDetails = () => {
  const { updateStepStatus, setCurrentStep } = useWizardInfo();
  const { formData, setFormData, setFieldValue } = useFormWithProvider<CreateLoanRequestFormData>();
  const peopleId = useSelector(loggedInUserSelectors.peopleId);

  const { data: basicData } = useQuery({
    queryKey: [BASIC_LOAN_DATA_KEY, peopleId],
    queryFn: () => api.loans.basicUserLoanData(peopleId!),
    enabled: !!peopleId,
  });

  const startDateOptions = React.useMemo(() => {
    const baseDate = basicData?.last_payroll_month
      ? new Date(basicData.last_payroll_month)
      : new Date();
    baseDate.setDate(1);
    return Array.from({ length: 3 }, (_, i) => {
      const monthDate = new Date(baseDate);
      monthDate.setMonth(monthDate.getMonth() + i);
      const value = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(
        2,
        '0',
      )}-01`;
      return { label: formatMonthYear(value), value };
    });
  }, [basicData?.last_payroll_month]);

  const isNextDisabled =
    !formData.principal ||
    !(Number(formData.principal) > 0) ||
    !formData.tenure ||
    !formData.recoveryStartFrom;

  const handleNext = () => {
    updateStepStatus(1, 'success');
    setCurrentStep(2);
  };

  return (
    <Box display="flex" flex={1} overflow="hidden">
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
        style={{ flex: 1, minWidth: 0 }}>
        <StepContentWrapper noBgIllustration width="462px">
          <Box display="flex" flexDirection="column" gap="spacing.8">
            <Box>
              <Heading size="large" weight="semibold" color="surface.text.gray.normal">
                Amount Details
              </Heading>
              <Text
                size="medium"
                color="surface.text.gray.muted"
                variant="body"
                weight="regular"
                marginTop="spacing.2">
                Configure your advance details. Please follow the policy guidelines for faster
                approval.
              </Text>
            </Box>

            <TextInput
              label="Requested Amount"
              placeholder="Enter amount required"
              necessityIndicator="required"
              prefix="₹"
              value={formData.principal}
              onChange={({ value }) => {
                if (value && !WHOLE_NUMBER_REGEX.test(value)) {
                  return;
                }
                setFieldValue('principal', value ?? '');
              }}
              helpText="Final approved amount may vary based on review"
              type="number"
            />

            <Box display="flex" flexDirection="column" gap="spacing.3">
              <Text size="small" variant="body" weight="semibold" color="surface.text.gray.muted">
                Repayment Tenure
              </Text>
              <Text size="medium" variant="body" weight="regular">
                {formData.tenure ?? 1} Month{(formData.tenure ?? 1) > 1 ? '(s)' : ''}
              </Text>
              <input
                type="range"
                min={1}
                max={24}
                value={formData.tenure}
                onChange={(e) => setFieldValue('tenure', Number(e.target.value))}
                style={{ width: '100%', accentColor: '#5469D4' }}
              />
              <Box display="flex" justifyContent="space-between">
                <Text size="xsmall" color="surface.text.gray.muted">
                  1mo
                </Text>
                <Text size="xsmall" color="surface.text.gray.muted">
                  24mo
                </Text>
              </Box>
            </Box>

            <Dropdown selectionType="single">
              <SelectInput
                label="Repayment Start Date"
                placeholder="Select month"
                necessityIndicator="required"
                value={formData.recoveryStartFrom || undefined}
                onChange={({ values }) => setFieldValue('recoveryStartFrom', values[0] ?? '')}
                helpText="Choose when you want to start EMI deductions"
              />
              <DropdownOverlay>
                <ActionList>
                  {startDateOptions.map((opt) => (
                    <ActionListItem key={opt.value} title={opt.label} value={opt.value} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          </Box>
        </StepContentWrapper>

        <StepActionsWrapper width="462px">
          <Button variant="tertiary" onClick={() => setCurrentStep(0)}>
            Previous
          </Button>
          <Button type="submit" isDisabled={isNextDisabled}>
            Next
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      <PolicySummaryPanel
        policySummary={formData.policySummary}
        policyFileId={formData.policyFileId}
      />
    </Box>
  );
};

export default AmountDetails;
